import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Print as PrintIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  AccessTime as RegularIcon,
  TrendingUp as OvertimeIcon,
  EventBusy as OffDayIcon,
  WbSunny as HolidayIcon,
  Groups as EmployeesIcon,
  Timer as TotalIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import timeSheetsService from '../services/timesheets.service';
import { branchesService } from '../services/branches.service';
import employeesService from '../services/employees.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const translations = {
  en: {
    title: 'TimeSheet Report',
    titleAr: 'تقرير الحضور والانصراف',
    fromDate: 'From Date',
    toDate: 'To Date',
    branch: 'Branch',
    employee: 'Employee',
    allBranches: 'All Branches',
    allEmployees: 'All Employees',
    generate: 'Generate Report',
    print: 'Print',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    refresh: 'Refresh',
    employeeCode: 'Code',
    employeeName: 'Employee',
    branchName: 'Branch',
    regularHours: 'Regular',
    overtimeHours: 'Overtime',
    offHours: 'Off Day',
    publicHolidayHours: 'Holiday',
    totalHours: 'Total',
    summary: 'Summary',
    totalEmployees: 'Total Employees',
    dailyBreakdown: 'Daily Breakdown',
    date: 'Date',
    day: 'Day',
    loading: 'Loading...',
    noData: 'No data found',
    reportGenerated: 'Report Generated',
    asOf: 'As of',
    period: 'Period',
    hours: 'hrs',
  },
  ar: {
    title: 'تقرير الحضور والانصراف',
    titleAr: 'تقرير الحضور والانصراف',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    branch: 'الفرع',
    employee: 'الموظف',
    allBranches: 'جميع الفروع',
    allEmployees: 'جميع الموظفين',
    generate: 'إنشاء التقرير',
    print: 'طباعة',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    refresh: 'تحديث',
    employeeCode: 'الكود',
    employeeName: 'الموظف',
    branchName: 'الفرع',
    regularHours: 'عادي',
    overtimeHours: 'إضافي',
    offHours: 'كانسل أوف',
    publicHolidayHours: 'عطل رسمية',
    totalHours: 'الإجمالي',
    summary: 'الملخص',
    totalEmployees: 'إجمالي الموظفين',
    dailyBreakdown: 'التفصيل اليومي',
    date: 'التاريخ',
    day: 'اليوم',
    loading: 'جاري التحميل...',
    noData: 'لا توجد بيانات',
    reportGenerated: 'تم إنشاء التقرير',
    asOf: 'بتاريخ',
    period: 'الفترة',
    hours: 'ساعة',
  },
};

interface TimeSheetReportData {
  reportDate: string;
  fromDate: string;
  toDate: string;
  branchId?: number;
  branchName?: string;
  branchNameAr?: string;
  employeeId?: number;
  employeeCode?: string;
  employeeName?: string;
  employeeNameAr?: string;
  totalEmployees: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalOffHours: number;
  totalPublicHolidayHours: number;
  totalHours: number;
  employees: EmployeeTimeSheetData[];
}

interface EmployeeTimeSheetData {
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  employeeNameAr: string;
  branchName: string;
  branchNameAr: string;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalOffHours: number;
  totalPublicHolidayHours: number;
  totalHours: number;
  dailyData: DailyTimeSheetData[];
}

interface DailyTimeSheetData {
  workDate: string;
  dayName: string;
  regularHours: number;
  overtimeHours: number;
  offHours: number;
  publicHolidayHours: number;
  totalHours: number;
  isFriday: boolean;
}

const TimeSheetReport: React.FC = () => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const { minDate, maxDate } = useFiscalDateRange();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<TimeSheetReportData | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  
  // Filters
  const [fromDate, setFromDate] = useState<string>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [employeeId, setEmployeeId] = useState<number | ''>('');
  
  const [branches, setBranches] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);

  useEffect(() => {
    loadCompanyInfo();
    loadBranches();
    loadEmployees();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();
      setBranches(data);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await employeesService.getAll();
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  // Filter employees when branch changes
  const filteredEmployees = branchId 
    ? employees.filter(emp => emp.branchId === branchId)
    : employees;

  const generateReport = async () => {
    try {
      setLoading(true);
      const params: any = {
        fromDate,
        toDate,
      };
      if (branchId) params.branchId = branchId;
      if (employeeId) params.employeeId = employeeId;

      const response = await timeSheetsService.getReport(params);
      setReportData(response);
      toast.success(t.reportGenerated);
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error(t.noData);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value: number) => value.toFixed(2);

  const exportToExcel = async () => {
    if (!reportData) return;
    
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      let currentRow = 1;

      // Company Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 80, height: 60 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      // Company Header
      worksheet.mergeCells('B1:H1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FF0288D1' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('B2:H2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = `📍 ${companyInfo?.address || ''}`;
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('B3:H3');
      const contactCell = worksheet.getCell('B3');
      contactCell.value = `📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };

      currentRow = 5;

      // Report Title
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF0288D1' }
      };

      currentRow++;

      // Period Info
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      const periodCell = worksheet.getCell(`A${currentRow}`);
      periodCell.value = `${t.period}: ${fromDate} → ${toDate}`;
      periodCell.font = { size: 10, bold: true, color: { argb: 'FF0288D1' } };
      periodCell.alignment = { horizontal: 'center' };

      currentRow++;

      // Filters
      let filterText = '';
      if (reportData.branchName) {
        filterText = `${t.branch}: ${isRTL ? reportData.branchNameAr : reportData.branchName}`;
      }
      if (reportData.employeeName) {
        if (filterText) filterText += ' | ';
        filterText += `${t.employee}: ${isRTL ? reportData.employeeNameAr : reportData.employeeName}`;
      }

      if (filterText) {
        worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
        const filterCell = worksheet.getCell(`A${currentRow}`);
        filterCell.value = `🔍 ${filterText}`;
        filterCell.font = { size: 9, color: { argb: 'FF0288D1' } };
        filterCell.alignment = { horizontal: 'center' };
        currentRow++;
      }

      currentRow++;

      // Summary Cards
      const summaryRow = currentRow;
      worksheet.mergeCells(`A${summaryRow}:B${summaryRow}`);
      const empCell = worksheet.getCell(`A${summaryRow}`);
      empCell.value = `👥 ${t.totalEmployees}: ${reportData.totalEmployees}`;
      empCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      empCell.alignment = { horizontal: 'center', vertical: 'middle' };
      empCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } };

      worksheet.mergeCells(`C${summaryRow}:D${summaryRow}`);
      const regCell = worksheet.getCell(`C${summaryRow}`);
      regCell.value = `⏰ ${t.regularHours}: ${formatNumber(reportData.totalRegularHours)}`;
      regCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      regCell.alignment = { horizontal: 'center', vertical: 'middle' };
      regCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2196F3' } };

      worksheet.mergeCells(`E${summaryRow}:F${summaryRow}`);
      const overtimeCell = worksheet.getCell(`E${summaryRow}`);
      overtimeCell.value = `📈 ${t.overtimeHours}: ${formatNumber(reportData.totalOvertimeHours)}`;
      overtimeCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      overtimeCell.alignment = { horizontal: 'center', vertical: 'middle' };
      overtimeCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF9800' } };

      worksheet.mergeCells(`G${summaryRow}:H${summaryRow}`);
      const totalCell = worksheet.getCell(`G${summaryRow}`);
      totalCell.value = `⏱️ ${t.totalHours}: ${formatNumber(reportData.totalHours)}`;
      totalCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      totalCell.alignment = { horizontal: 'center', vertical: 'middle' };
      totalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF9C27B0' } };

      currentRow += 2;

      // Table Headers
      const headerRow = worksheet.getRow(currentRow);
      headerRow.values = [
        t.employeeCode,
        t.employeeName,
        t.branchName,
        t.regularHours,
        t.overtimeHours,
        t.offHours,
        t.publicHolidayHours,
        t.totalHours
      ];
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF0288D1' }
      };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;

      currentRow++;

      // Employee Data
      reportData.employees.forEach((emp) => {
        const row = worksheet.getRow(currentRow);
        row.values = [
          emp.employeeCode,
          isRTL ? emp.employeeNameAr : emp.employeeName,
          isRTL ? emp.branchNameAr : emp.branchName,
          formatNumber(emp.totalRegularHours),
          formatNumber(emp.totalOvertimeHours),
          formatNumber(emp.totalOffHours),
          formatNumber(emp.totalPublicHolidayHours),
          formatNumber(emp.totalHours)
        ];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        
        // Alternate row colors
        if (currentRow % 2 === 0) {
          row.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF5F5F5' }
          };
        }
        
        currentRow++;
      });

      // Column widths
      worksheet.getColumn(1).width = 15;
      worksheet.getColumn(2).width = 30;
      worksheet.getColumn(3).width = 20;
      worksheet.getColumn(4).width = 15;
      worksheet.getColumn(5).width = 15;
      worksheet.getColumn(6).width = 15;
      worksheet.getColumn(7).width = 15;
      worksheet.getColumn(8).width = 15;

      // Save file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TimeSheet_Report_${fromDate}_${toDate}.xlsx`;
      link.click();

      toast.success(t.exportExcel + ' ✓');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل تصدير Excel' : 'Excel export failed');
    }
  };

  const exportToPdf = async () => {
    if (!reportData) return;

    try {
      // استيراد html2canvas
      const html2canvas = (await import('html2canvas')).default;

      const doc = new jsPDF('l', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 10;

      // Add Logo if available
      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', pageWidth / 2 - 15, yPosition, 30, 20);
          yPosition += 25;
        } catch (err) {
          console.warn('Could not add logo to PDF:', err);
        }
      }

      // Get selected branch name
      const selectedBranch = branchId ? branches.find(b => b.branchId === branchId) : null;
      const branchName = selectedBranch ? (isRTL ? selectedBranch.branchNameAr : selectedBranch.branchName) : (isRTL ? 'جميع الفروع' : 'All Branches');

      // Company Header as Image for Arabic support
      const headerDiv = document.createElement('div');
      headerDiv.style.cssText = 'position: absolute; left: -9999px; width: 280mm; padding: 20px; text-align: center; font-family: Arial, sans-serif; background: white;';
      headerDiv.innerHTML = `
        <div style="font-size: 24px; font-weight: bold; color: #0288D1; margin-bottom: 8px;">
          ${isRTL ? companyInfo?.companyNameAr || companyInfo?.companyName : companyInfo?.companyName}
        </div>
        <div style="font-size: 12px; color: #424242; margin-bottom: 4px;">
          ${companyInfo?.address || ''}
        </div>
        <div style="font-size: 12px; color: #424242;">
          ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}
        </div>
        <div style="font-size: 18px; font-weight: bold; color: #0288D1; margin-top: 12px; padding: 8px; background: #E3F2FD; border-radius: 4px;">
          ${t.title}
        </div>
        <div style="font-size: 14px; color: #0288D1; margin-top: 8px;">
          ${t.period}: ${fromDate} → ${toDate}
        </div>
        <div style="font-size: 13px; color: #424242; margin-top: 6px; font-weight: bold;">
          ${isRTL ? 'الفرع' : 'Branch'}: ${branchName}
        </div>
      `;
      
      document.body.appendChild(headerDiv);
      const headerCanvas = await html2canvas(headerDiv, { scale: 2, backgroundColor: '#ffffff' });
      document.body.removeChild(headerDiv);

      const headerImgData = headerCanvas.toDataURL('image/png');
      const headerHeight = 50;
      doc.addImage(headerImgData, 'PNG', 10, yPosition, pageWidth - 20, headerHeight);
      yPosition += headerHeight + 5;

      // Table as Image for Arabic support
      const tableDiv = document.createElement('div');
      tableDiv.style.cssText = 'position: absolute; left: -9999px; width: 280mm; padding: 10px; font-family: Arial, sans-serif; background: white;';
      
      let tableHTML = `
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <thead>
            <tr style="background: #0288D1; color: white;">
              <th style="border: 2px solid #333; padding: 8px; text-align: center; font-weight: bold;">${t.employeeCode}</th>
              <th style="border: 2px solid #333; padding: 8px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: bold;">${t.employeeName}</th>
              <th style="border: 2px solid #333; padding: 8px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: bold;">${t.branchName}</th>
              <th style="border: 2px solid #333; padding: 8px; text-align: right; font-weight: bold;">${t.regularHours}</th>
              <th style="border: 2px solid #333; padding: 8px; text-align: right; font-weight: bold;">${t.overtimeHours}</th>
              <th style="border: 2px solid #333; padding: 8px; text-align: right; font-weight: bold;">${t.offHours}</th>
              <th style="border: 2px solid #333; padding: 8px; text-align: right; font-weight: bold;">${t.publicHolidayHours}</th>
              <th style="border: 2px solid #333; padding: 8px; text-align: right; background: #9C27B0; font-weight: bold;">${t.totalHours}</th>
            </tr>
          </thead>
          <tbody>
      `;

      reportData.employees.forEach((emp: any, index: number) => {
        const rowColor = index % 2 === 0 ? '#E3F2FD' : '#F0F0F0';
        tableHTML += `
          <tr style="background: ${rowColor};">
            <td style="border: 2px solid #333; padding: 6px; text-align: center; color: #000000; font-weight: 500;">${emp.employeeCode}</td>
            <td style="border: 2px solid #333; padding: 6px; color: #000000; font-weight: 500;">${isRTL ? emp.employeeNameAr : emp.employeeName}</td>
            <td style="border: 2px solid #333; padding: 6px; color: #000000; font-weight: 500;">${isRTL ? emp.branchNameAr : emp.branchName}</td>
            <td style="border: 2px solid #333; padding: 6px; text-align: right; color: #000000; font-weight: 500;">${formatNumber(emp.totalRegularHours)}</td>
            <td style="border: 2px solid #333; padding: 6px; text-align: right; color: #000000; font-weight: 500;">${formatNumber(emp.totalOvertimeHours)}</td>
            <td style="border: 2px solid #333; padding: 6px; text-align: right; color: #000000; font-weight: 500;">${formatNumber(emp.totalOffHours)}</td>
            <td style="border: 2px solid #333; padding: 6px; text-align: right; color: #000000; font-weight: 500;">${formatNumber(emp.totalPublicHolidayHours)}</td>
            <td style="border: 2px solid #333; padding: 6px; text-align: right; font-weight: bold; background: #E1BEE7; color: #000000;">${formatNumber(emp.totalHours)}</td>
          </tr>
        `;
      });

      tableHTML += `</tbody></table>`;
      tableDiv.innerHTML = tableHTML;
      document.body.appendChild(tableDiv);

      const tableCanvas = await html2canvas(tableDiv, { scale: 2, backgroundColor: '#ffffff' });
      document.body.removeChild(tableDiv);

      const tableImgData = tableCanvas.toDataURL('image/png');
      const tableImgWidth = pageWidth - 20;
      const tableImgHeight = (tableCanvas.height * tableImgWidth) / tableCanvas.width;

      // Check if need multiple pages
      let heightLeft = tableImgHeight;
      let position = yPosition;

      doc.addImage(tableImgData, 'PNG', 10, position, tableImgWidth, tableImgHeight);
      heightLeft -= (pageHeight - position - 10);

      while (heightLeft > 0) {
        position = heightLeft - tableImgHeight + 10;
        doc.addPage();
        doc.addImage(tableImgData, 'PNG', 10, position, tableImgWidth, tableImgHeight);
        heightLeft -= (pageHeight - 10);
      }

      doc.save(`TimeSheet_Report_${fromDate}_${toDate}.pdf`);
      toast.success(t.exportPdf + ' ✓');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error(isRTL ? 'فشل تصدير PDF' : 'PDF export failed');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!reportData && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#0288D1' }}>
            {t.title}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.fromDate}
                  value={fromDate ? new Date(fromDate) : null}
                  onChange={(date) => {
                    const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                    setFromDate(newVal);
                    if (toDate && newVal && toDate < newVal) setToDate('');
                  }}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={toDate ? new Date(toDate) : maxDate}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.toDate}
                  value={toDate ? new Date(toDate) : null}
                  onChange={(date) => {
                    const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                    setToDate(newVal);
                    if (fromDate && newVal && fromDate > newVal) setFromDate('');
                  }}
                  format="dd/MM/yyyy"
                  minDate={fromDate ? new Date(fromDate) : minDate}
                  maxDate={maxDate}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <TextField
                fullWidth
                select
                label={t.branch}
                value={branchId}
                onChange={(e) => {
                  setBranchId(Number(e.target.value));
                  setEmployeeId(''); // Reset employee when branch changes
                }}
              >
                <MenuItem value="">{t.allBranches}</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {isRTL ? branch.branchNameAr : branch.branchName}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <TextField
                fullWidth
                select
                label={t.employee}
                value={employeeId}
                onChange={(e) => setEmployeeId(Number(e.target.value))}
              >
                <MenuItem value="">{t.allEmployees}</MenuItem>
                {filteredEmployees.map((emp) => (
                  <MenuItem key={emp.employeeId} value={emp.employeeId}>
                    {emp.employeeCode} - {isRTL ? emp.fullNameAr : emp.fullName}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Button
            variant="contained"
            onClick={generateReport}
            disabled={loading}
            sx={{ bgcolor: '#0288D1', '&:hover': { bgcolor: '#0277BD' } }}
          >
            {loading ? <CircularProgress size={24} /> : t.generate}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="no-print">
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0288D1' }}>
          {t.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t.refresh}>
            <IconButton onClick={() => setReportData(null)} sx={{ color: '#0288D1' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportExcel}>
            <IconButton onClick={exportToExcel} sx={{ color: '#4CAF50' }}>
              <ExcelIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportPdf}>
            <IconButton onClick={exportToPdf} sx={{ color: '#F44336' }}>
              <PdfIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.print}>
            <IconButton onClick={handlePrint} sx={{ color: '#2196F3' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {reportData && (
        <>
          {/* Company Header (Print) */}
          <Box sx={{ display: 'none' }} className="print-only">
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              {companyInfo?.logoBase64 && (
                <img src={companyInfo.logoBase64} alt="Logo" style={{ width: 80, height: 60 }} />
              )}
              <Typography variant="h4" sx={{ color: '#0288D1', fontWeight: 'bold' }}>
                {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
              </Typography>
              <Typography variant="body2">{companyInfo?.address}</Typography>
              <Typography variant="body2">{companyInfo?.phone} | {companyInfo?.email}</Typography>
              <Typography variant="h5" sx={{ mt: 2, color: '#0288D1' }}>{t.title}</Typography>
              <Typography variant="body2">{t.period}: {fromDate} → {toDate}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                {isRTL ? 'الفرع' : 'Branch'}: {
                  branchId 
                    ? (isRTL ? branches.find(b => b.branchId === branchId)?.branchNameAr : branches.find(b => b.branchId === branchId)?.branchName)
                    : (isRTL ? 'جميع الفروع' : 'All Branches')
                }
              </Typography>
            </Box>
          </Box>

          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }} className="no-print">
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <Card sx={{ bgcolor: '#4CAF50', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmployeesIcon sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {reportData.totalEmployees}
                      </Typography>
                      <Typography variant="body2">{t.totalEmployees}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <Card sx={{ bgcolor: '#2196F3', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RegularIcon sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {formatNumber(reportData.totalRegularHours)}
                      </Typography>
                      <Typography variant="body2">{t.regularHours}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <Card sx={{ bgcolor: '#FF9800', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OvertimeIcon sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {formatNumber(reportData.totalOvertimeHours)}
                      </Typography>
                      <Typography variant="body2">{t.overtimeHours}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <Card sx={{ bgcolor: '#9C27B0', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TotalIcon sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {formatNumber(reportData.totalHours)}
                      </Typography>
                      <Typography variant="body2">{t.totalHours}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Data Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#0288D1' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.employeeCode}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.employeeName}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.branchName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <RegularIcon fontSize="small" />
                      {t.regularHours}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <OvertimeIcon fontSize="small" />
                      {t.overtimeHours}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <OffDayIcon fontSize="small" />
                      {t.offHours}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <HolidayIcon fontSize="small" />
                      {t.publicHolidayHours}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <TotalIcon fontSize="small" />
                      {t.totalHours}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.employees.map((emp, index) => (
                  <TableRow
                    key={emp.employeeId}
                    sx={{ 
                      bgcolor: index % 2 === 0 
                        ? (mode === 'dark' ? '#1E3A5F' : '#E3F2FD') 
                        : (mode === 'dark' ? '#263238' : '#FFFFFF'),
                      '&:hover': { bgcolor: mode === 'dark' ? '#37474F' : '#BBDEFB' }
                    }}
                  >
                    <TableCell>{emp.employeeCode}</TableCell>
                    <TableCell>{isRTL ? emp.employeeNameAr : emp.employeeName}</TableCell>
                    <TableCell>{isRTL ? emp.branchNameAr : emp.branchName}</TableCell>
                    <TableCell align="right">
                      <Chip label={formatNumber(emp.totalRegularHours)} size="small" sx={{ bgcolor: '#E3F2FD', color: '#2196F3' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={formatNumber(emp.totalOvertimeHours)} size="small" sx={{ bgcolor: '#FFF3E0', color: '#FF9800' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={formatNumber(emp.totalOffHours)} size="small" sx={{ bgcolor: '#F3E5F5', color: '#9C27B0' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={formatNumber(emp.totalPublicHolidayHours)} size="small" sx={{ bgcolor: '#FFF9C4', color: '#F57C00' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={formatNumber(emp.totalHours)} size="small" sx={{ bgcolor: '#9C27B0', color: 'white', fontWeight: 'bold' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default TimeSheetReport;

// Print Styles
const printStyles = `
@media print {
  .no-print {
    display: none !important;
  }
  .print-only {
    display: block !important;
  }
  /* Hide Layout header and sidebar */
  header, nav, aside, .MuiAppBar-root, .MuiDrawer-root, [role="navigation"] {
    display: none !important;
  }
  /* Reset body and main content */
  body {
    margin: 0 !important;
    padding: 0 !important;
  }
  main {
    margin: 0 !important;
    padding: 20px !important;
  }
  @page {
    margin: 15mm;
  }
}
`;

// Inject print styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = printStyles;
  document.head.appendChild(style);
}